// src/components/dashboards/charts/StagingSunburst.tsx
// Sunburst chart: Domain → T → N → M staging hierarchy.

import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import ReactECharts from "echarts-for-react";
import useChartTheme, { mixToward } from "./useChartTheme";
import { athenaColors } from "../../../theme/palette";
import { StagingData, StagingNode } from "hooks/useAnalytics";
import { useTranslation } from "../../../i18n";

interface Props {
  data: StagingData | null;
  loading: boolean;
}

const StagingSunburst: React.FC<Props> = ({ data, loading }) => {
  const { baseTooltip, seriesColors, textPrimary, textSecondary, isMobile, dark } = useChartTheme();
  const { t } = useTranslation();

  const h = isMobile ? 260 : 340;

  if (loading || !data) {
    return <Skeleton variant="rounded" height={h} sx={{ borderRadius: 2 }} />;
  }

  if (!data.children || data.children.length === 0) {
    return (
      <Box sx={{ height: h, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {t("dashboard.analytics.no_staging_data", "No staging data available")}
        </Typography>
      </Box>
    );
  }

  // ECharts sunburst makes every descendant inherit its ancestor's colour, and
  // this cohort has ONE top-level node (a single cancer type), so the whole
  // ring came out as one flat orange disc. Colour at depth 2, where the actual
  // T stages branch, and the rings differentiate.
  // In line with the rest of the page: the bars, the pie and the completeness
  // chart are all blue-to-purple, and the sunburst was the only panel throwing
  // saturated orange wedges. Restricted to the blue/purple half of the brand
  // ramp, with each level a little more transparent than the one inside it so
  // the disc still has depth without needing a second hue.
  const STAGE_RAMP = [
    athenaColors.periwinkle,
    athenaColors.lightPurple,
    mixToward(athenaColors.royalBlue, athenaColors.periwinkle, 0.55),
    athenaColors.softPurple,
    mixToward(athenaColors.royalBlue, athenaColors.periwinkle, 0.3),
    mixToward(athenaColors.lightPurple, athenaColors.periwinkle, 0.55),
  ];
  const ramp = dark ? STAGE_RAMP : seriesColors;

  const paint = (node: StagingNode, color: string): Record<string, unknown> => ({
    ...node,
    itemStyle: { color },
    children: node.children?.map((c) => paint(c, color)),
  });
  const coloured = data.children.map((root, ri) => ({
    ...root,
    itemStyle: { color: ramp[ri % ramp.length] },
    children: (root.children || []).map((child, ci) =>
      // Painted down the whole branch, not just this ring: ECharts only
      // inherits a colour when the child has none, and the root-level
      // `color` palette was claiming the outer rings first, so they came
      // back as one flat orange band regardless of which T stage they
      // belonged to.
      paint(child, ramp[(ci + 1) % ramp.length]),
    ),
  }));

  const option = {
    title: {
      text: t("dashboard.analytics.staging_overview", "TNM Staging"),
      subtext: t(
        "dashboard.analytics.staging_overview_sub",
        "Distribution across TNM stages",
      ),
      textStyle: { color: textPrimary, fontSize: 14, fontWeight: 600 },
      subtextStyle: { color: textSecondary, fontSize: 11 },
      left: 0,
    },
    tooltip: { ...baseTooltip, trigger: "item" },
    series: [
      {
        type: "sunburst",
        data: coloured,
        radius: ["14%", "84%"],
        sort: undefined,
        emphasis: { focus: "ancestor" },
        levels: [
          {},
          {
            r0: "15%", r: "40%",
            itemStyle: { borderWidth: 2 },
            // Bounded, not just unrotated: the root spans the full circle, so
            // its label lands on the hub where the T-stage labels converge.
            label: {
              fontSize: 11,
              color: "#fff",
              fontWeight: 600,
              rotate: 0,
              overflow: "truncate",
              width: 88,
              // Overlap is avoidable, and this is the fix: a segment narrower
              // than this many degrees cannot hold its own text, so ECharts
              // drew them all on top of each other around the hub. The tooltip
              // still names every segment.
              minAngle: 12,
            },
          },
          {
            r0: "40%", r: "62%",
            label: {
              fontSize: 10,
              color: "#fff",
              overflow: "truncate",
              width: 46,
              minAngle: 14,
            },
          },
          // Depth 3 and 4 are dozens of two-character stage codes on rings a
          // few pixels tall: at 9px they overlapped into an unreadable hatch
          // across the whole disc. The tooltip still names every segment.
          {
            r0: "62%", r: "80%",
            label: { show: false },
            itemStyle: { opacity: 0.82 },
          },
          {
            r0: "80%", r: "90%",
            label: { show: false },
            itemStyle: { opacity: 0.66 },
          },
        ],
        itemStyle: {
          borderColor: "rgba(255,255,255,0.18)",
          borderWidth: 1,
        },
      },
    ],
    color: seriesColors,
  };

  return (
    <Box sx={{ width: "100%", height: h }}>
      <ReactECharts option={option} style={{ width: "100%", height: "100%" }} opts={{ renderer: "canvas" }} />
    </Box>
  );
};

export default StagingSunburst;
